import { Box, Collapse, Divider, DrawerBody, DrawerHeader, FormLabel } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import { DrawerFixedButton } from 'components/Drawer';
import { FormikCheckbox, FormikInput } from 'components/Formik';
import RadioInput from 'components/RadioCard';
import { useSaveBreakRule, ISaveBreakRule } from 'hooks/breakRules/useSaveBreakRules';

import { initialBreakData, breakRuleFormValidation } from './schema';

interface IAddBreak {
  onClose: () => void;
  formData?: typeof initialBreakData;
}

const AddBreak: React.FC<IAddBreak> = ({ onClose, formData = initialBreakData }) => {
  const { t } = useTranslation(['common', 'companySettings']);

  const [paid, setPaid] = useState<string>(formData.paid ? 'paid' : 'unpaid');

  const { status, mutateAsync } = useSaveBreakRule();

  const formattedData: any = {
    id: formData?.id ? formData.id : '',
    name: formData?.name ? formData.name : '',
    length: formData?.length ? formData.length / 60 : 0,
    paid: formData?.paid ? 'paid' : 'unpaid',
    end_break_automatically: formData?.end_break_automatically ? ['1'] : [],
    cannot_end_early: formData?.cannot_end_early ? ['1'] : [],
    no_set_length: formData?.no_set_length ? ['1'] : [],
    notify_before_break_ends: formData?.notify_before_break_ends ? ['1'] : [],
  };

  const typeOptions = useMemo(
    () => [
      {
        label: t('common:paid'),
        value: 'paid',
      },
      {
        label: t('common:unpaid'),
        value: 'unpaid',
      },
    ],
    [t]
  );

  const handleSetLengthChange = (
    e: any,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    if (e.target.checked) {
      setFieldValue('toggle_no_set_length', false);
      return;
    }
    setFieldValue('toggle_no_set_length', true);
  };

  const handleFormSubmit = async (values: typeof initialBreakData) => {
    const postData: ISaveBreakRule = {
      id: values.id,
      name: values.name,
      length: values.length,
      paid: paid === 'paid' ? true : false,
      no_set_length: values?.no_set_length.length ? true : false,
      end_break_automatically: !values?.no_set_length.length && values?.end_break_automatically.length ? true : false,
      cannot_end_early: !values?.no_set_length.length && values?.cannot_end_early.length ? true : false,
      notify_before_break_ends: !values?.no_set_length.length && values?.notify_before_break_ends.length ? true : false,
    };
    mutateAsync(postData).then(() => {
      if (onClose) {
        onClose();
      }
    });
  };

  return (
    <>
      <DrawerHeader>
        {formattedData.id ? t('companySettings:updateBreakRule') : t('companySettings:addBreakRule')}
      </DrawerHeader>
      <DrawerBody>
        <Divider width="100%" />
        <Formik
          enableReinitialize
          initialValues={formattedData}
          validationSchema={breakRuleFormValidation}
          onSubmit={handleFormSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form noValidate>
              <Box mt={6}>
                <FormikInput
                  type="text"
                  name="name"
                  label={t('companySettings:breakName')}
                  formControlProps={{ isRequired: true }}
                />
                <FormikInput
                  type="number"
                  name="length"
                  label={t('companySettings:length(inMinutes)')}
                  formControlProps={{ isDisabled: values.no_set_length.length }}
                />
                <FormikCheckbox
                  options={[{ label: t('companySettings:noSetLength'), value: '1' }]}
                  name="no_set_length"
                  stackProps={{ mr: 5 }}
                  onChangeCallback={e => {
                    if (e.target.checked) {
                      setFieldValue('length', 0);
                    }

                    handleSetLengthChange(e, setFieldValue);
                  }}
                />
                <Box>
                  <FormLabel fontSize="14px" fontWeight={600} margin="0 11px 0 0">
                    {t('common:type')}
                  </FormLabel>
                  <RadioInput
                    variant="secondary"
                    options={typeOptions}
                    name="paid"
                    defaultValue={paid}
                    handleChange={setPaid}
                  ></RadioInput>
                </Box>
              </Box>
              <Collapse in={!values.no_set_length.length} unmountOnExit={true} animateOpacity>
                <Box mt={6}>
                  <FormikCheckbox
                    options={[{ label: t('companySettings:workersCantEndBreakEarly'), value: '1' }]}
                    name="cannot_end_early"
                    stackProps={{ mr: 5 }}
                  />
                  <FormikCheckbox
                    options={[{ label: t('companySettings:automaticallyEndBreakWhenDurationEnds'), value: '1' }]}
                    name="end_break_automatically"
                    stackProps={{ mr: 5 }}
                  />
                  <FormikCheckbox
                    options={[{ label: t('companySettings:notifyAUserWhenBreakEnds'), value: '1' }]}
                    name="notify_before_break_ends"
                    stackProps={{ mr: 5 }}
                  />
                </Box>
              </Collapse>

              <DrawerFixedButton>
                <Button colorScheme="secondary" type="submit" width="100%" size="lg" isLoading={status === 'loading'}>
                  {formattedData.id ? t('companySettings:updateBreak') : t('companySettings:addBreak')}
                </Button>
              </DrawerFixedButton>
            </Form>
          )}
        </Formik>
      </DrawerBody>
    </>
  );
};
export default AddBreak;
